import { useEffect } from 'react';

/**
 * Hook to safely load external scripts
 * @param {Object} options - Script configuration options
 * @param {string} options.src - Script source URL
 * @param {string} options.containerId - ID of container to append script to
 * @param {Object} options.attributes - Additional attributes to add to script tag
 * @param {number} options.delay - Delay in ms before attempting to add script
 * @returns {Object} - Status object with loading state
 */
const useScript = ({ src, containerId, attributes = {}, delay = 100 }) => {
  useEffect(() => {
    // Check if script already exists to prevent duplicates
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      console.log(`Script ${src} already loaded, not adding again`);
      return;
    }

    const timeoutId = setTimeout(() => {
      const container = document.getElementById(containerId);
      
      if (!container) {
        console.log(`Container with ID "${containerId}" not found`);
        return;
      }
      
      // Clear container first to prevent duplicates
      container.innerHTML = '';
      
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      
      // Add any additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
      
      container.appendChild(script);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [src, containerId, attributes, delay]);
};

export default useScript; 